import { ApiResponse } from '@shared/types';
import {
  FunctionsFetchError,
  FunctionsHttpError,
  FunctionsRelayError,
} from '@supabase/supabase-js';
import {
  CreateCompanyUserRequest,
  ManagedUser,
  UpdateCompanyUserRoleRequest,
  UpdateCompanyUserStatusRequest,
  UserRoleMetric,
} from '../types/userManagement';
import { PaginatedResponse } from '../types/pagination';
import { supabase } from './supabase';

const DISABLED_ACCOUNT_ERROR =
  'This account has been disabled. Contact your administrator.';

const getFunctionErrorMessage = async (
  error: FunctionsHttpError | FunctionsRelayError | FunctionsFetchError | Error,
  fallbackMessage: string,
): Promise<string> => {
  if (error instanceof FunctionsHttpError) {
    try {
      const payload = await error.context.json();
      return (
        payload?.error || payload?.message || error.message || fallbackMessage
      );
    } catch {
      return error.message || fallbackMessage;
    }
  }

  if (
    error instanceof FunctionsRelayError ||
    error instanceof FunctionsFetchError
  ) {
    return error.message || fallbackMessage;
  }

  return error.message || fallbackMessage;
};

const mapManagedUser = (value: Record<string, unknown>): ManagedUser => ({
  id: String(value.id),
  email: String(value.email || ''),
  first_name: String(value.first_name || ''),
  last_name: String(value.last_name || ''),
  role: String(value.role) as ManagedUser['role'],
  outlet_id: (value.outlet_id as string | null) || null,
  outlet_name: (value.outlet_name as string | null) || null,
  outlet_business_type:
    (value.outlet_business_type as ManagedUser['outlet_business_type']) || null,
  company_id:
    typeof value.company_id === 'number'
      ? value.company_id
      : value.company_id
        ? Number(value.company_id)
        : null,
  is_active: Boolean(value.is_active),
  invited_by: (value.invited_by as string | null) || null,
  invited_by_user:
    value.invited_by_user && typeof value.invited_by_user === 'object'
      ? (value.invited_by_user as ManagedUser['invited_by_user'])
      : null,
  disabled_at: (value.disabled_at as string | null) || null,
  disabled_by: (value.disabled_by as string | null) || null,
  disabled_by_user:
    value.disabled_by_user && typeof value.disabled_by_user === 'object'
      ? (value.disabled_by_user as ManagedUser['disabled_by_user'])
      : null,
  created_at: String(value.created_at || ''),
  updated_at: String(value.updated_at || ''),
  has_passcode: Boolean(value.has_passcode),
  login_code: (value.login_code as string | null) || null,
});

export const fetchCompanyUsers = async (
  outletId?: string | null,
): Promise<ApiResponse<ManagedUser[]>> => {
  try {
    let query = supabase
      .from('company_users_view')
      .select('*')
      .order('outlet_name', { ascending: true })
      .order('first_name', { ascending: true })
      .order('last_name', { ascending: true });

    if (outletId) {
      query = query.eq('outlet_id', outletId);
    }

    const { data, error } = await query;

    if (error) {
      return { success: false, error: error.message, data: [] };
    }

    return {
      success: true,
      data: (data || []).map((user) => mapManagedUser(user)),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch company users',
      data: [],
    };
  }
};

export const fetchPaginatedCompanyUsers = async (opts: {
  outletId?: string | null;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<ApiResponse<PaginatedResponse<ManagedUser>>> => {
  const { outletId, search, page = 1, pageSize = 10 } = opts;
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  try {
    let query = supabase
      .from('company_users_view')
      .select('*', { count: 'exact' })
      .order('outlet_name', { ascending: true })
      .order('first_name', { ascending: true })
      .order('last_name', { ascending: true });

    if (outletId) {
      query = query.eq('outlet_id', outletId);
    }

    if (search?.trim()) {
      const term = search.trim();
      query = query.or(
        `first_name.ilike.%${term}%,last_name.ilike.%${term}%,email.ilike.%${term}%,outlet_name.ilike.%${term}%`,
      );
    }

    const { data, error, count } = await query.range(from, to);

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const totalItems = count || 0;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    return {
      success: true,
      data: {
        data: (data || []).map((user) => mapManagedUser(user)),
        pagination: {
          page,
          pageSize,
          totalItems,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch company users',
      data: undefined,
    };
  }
};

export const createCompanyUser = async (
  request: CreateCompanyUserRequest,
): Promise<ApiResponse<ManagedUser>> => {
  try {
    const { data, error } = await supabase.functions.invoke(
      'create-company-user',
      {
        body: {
          first_name: request.firstName.trim(),
          last_name: request.lastName.trim(),
          email: request.email.trim().toLowerCase(),
          password: request.password,
          outlet_id: request.outletId,
          role: request.role,
        },
      },
    );

    if (error) {
      return {
        success: false,
        error: await getFunctionErrorMessage(
          error,
          'Failed to create employee account',
        ),
        data: undefined,
      };
    }

    if (!data?.user) {
      return {
        success: false,
        error: 'User account was created but no user data was returned',
        data: undefined,
      };
    }

    return {
      success: true,
      data: mapManagedUser(data.user as Record<string, unknown>),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to create employee account',
      data: undefined,
    };
  }
};

export const updateCompanyUserStatus = async (
  request: UpdateCompanyUserStatusRequest,
): Promise<ApiResponse<ManagedUser>> => {
  try {
    const { data, error } = await supabase.functions.invoke(
      'update-company-user-status',
      {
        body: {
          user_id: request.userId,
          is_active: request.isActive,
        },
      },
    );

    if (error) {
      return {
        success: false,
        error: await getFunctionErrorMessage(
          error,
          'Failed to update employee status',
        ),
        data: undefined,
      };
    }

    if (!data?.user) {
      return {
        success: false,
        error: 'User status was updated but no user data was returned',
        data: undefined,
      };
    }

    return {
      success: true,
      data: mapManagedUser(data.user as Record<string, unknown>),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message || DISABLED_ACCOUNT_ERROR
          : 'Failed to update employee status',
      data: undefined,
    };
  }
};

export const updateCompanyUserRole = async (
  request: UpdateCompanyUserRoleRequest,
): Promise<ApiResponse<ManagedUser>> => {
  try {
    const { data, error } = await supabase.functions.invoke(
      'update-company-user-status',
      {
        body: {
          user_id: request.userId,
          role: request.role,
        },
      },
    );

    if (error) {
      return {
        success: false,
        error: await getFunctionErrorMessage(
          error,
          'Failed to update employee role',
        ),
        data: undefined,
      };
    }

    if (!data?.user) {
      return {
        success: false,
        error: 'Employee role was updated but no user data was returned',
        data: undefined,
      };
    }

    return {
      success: true,
      data: mapManagedUser(data.user as Record<string, unknown>),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message || DISABLED_ACCOUNT_ERROR
          : 'Failed to update employee role',
      data: undefined,
    };
  }
};

export const assignUserLoginCode = async (
  userId: string,
  loginCode: string | null,
): Promise<ApiResponse<ManagedUser>> => {
  try {
    const { data, error } = await supabase.functions.invoke('manage-passcode', {
      body: {
        action: loginCode ? 'set' : 'delete',
        target_user_id: userId,
        code: loginCode || undefined,
      },
    });

    if (error) {
      return {
        success: false,
        error: error.message || 'Failed to update employee passcode',
        data: undefined,
      };
    }

    if (data?.error) {
      return {
        success: false,
        error: data.error,
        data: undefined,
      };
    }

    // Fetch the updated user profile from company_users_view
    const { data: userData, error: fetchError } = await supabase
      .from('company_users_view')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    if (fetchError || !userData) {
      return {
        success: false,
        error:
          fetchError?.message ||
          'Passcode updated, but failed to reload employee profile.',
        data: undefined,
      };
    }

    return {
      success: true,
      data: mapManagedUser(userData),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to update employee passcode',
      data: undefined,
    };
  }
};

export const regenerateUserLoginCode = async (
  userId: string,
): Promise<ApiResponse<ManagedUser>> => {
  try {
    const { data, error } = await supabase.functions.invoke('manage-passcode', {
      body: {
        action: 'regenerate',
        target_user_id: userId,
      },
    });

    if (error) {
      return {
        success: false,
        error: error.message || 'Failed to regenerate employee passcode',
        data: undefined,
      };
    }

    if (data?.error) {
      return {
        success: false,
        error: data.error,
        data: undefined,
      };
    }

    // Fetch the updated user profile from company_users_view
    const { data: userData, error: fetchError } = await supabase
      .from('company_users_view')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    if (fetchError || !userData) {
      return {
        success: false,
        error:
          fetchError?.message ||
          'Passcode regenerated, but failed to reload employee profile.',
        data: undefined,
      };
    }

    return {
      success: true,
      data: mapManagedUser(userData),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to regenerate employee passcode',
      data: undefined,
    };
  }
};

export const fetchUserLoginCode = async (
  userId: string,
): Promise<ApiResponse<string | null>> => {
  try {
    const { data, error } = await supabase.functions.invoke('manage-passcode', {
      body: {
        action: 'get',
        target_user_id: userId,
      },
    });

    if (error) {
      return {
        success: false,
        error: error.message || 'Failed to retrieve employee passcode',
        data: null,
      };
    }

    if (data?.error) {
      return {
        success: false,
        error: data.error,
        data: null,
      };
    }

    return {
      success: true,
      data: data?.code || null,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to retrieve employee passcode',
      data: null,
    };
  }
};

/**
 * Fetch aggregated user count by roles from the view
 */
export const fetchUserRoleMetrics = async (
  outletId?: string | null,
): Promise<ApiResponse<UserRoleMetric[]>> => {
  try {
    let query = supabase.from('user_role_metrics_view').select('*');

    if (outletId) {
      query = query.eq('outlet_id', outletId);
    }

    const { data, error } = await query;

    if (error) {
      return { success: false, error: error.message, data: [] };
    }

    const mapped: UserRoleMetric[] = (data || []).map((row) => ({
      company_id: Number(row.company_id),
      outlet_id: (row.outlet_id as string | null) || null,
      role: row.role as UserRoleMetric['role'],
      user_count: Number(row.user_count),
    }));

    return {
      success: true,
      data: mapped,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to fetch user metrics',
      data: [],
    };
  }
};
